import { Helmet } from 'react-helmet';
import WaitlistForm from '@/components/WaitlistForm';

// Public landing for the street cards and the Instagram bio.
// Deliberately has NO prices, NO tiers and NO buy buttons: nothing can be sold
// until the sales agreement is executed. The real presale page lives at
// /build/preview and stays founder-gated until then.

const NAVY = 'var(--navy)';
const CREAM = 'var(--cream)';
const SERIF = 'var(--serif)';
const SANS = 'var(--sans)';
const MUTED = 'color-mix(in srgb, var(--navy) 62%, transparent)';
const RULE = 'color-mix(in srgb, var(--navy) 14%, transparent)';

const FACTS: [string, string][] = [
  ['Twelve private rooms', 'Eight by ten feet. Every one of them wheelchair accessible, so nobody waits for the accessible room.'],
  ['A self-cleaning toilet', 'With a bidet and a handheld sprayer, which most of the world has and we somehow do not.'],
  ['Real light at face height', 'A tall mirror lit for touch-ups, interviews and getting ready, plus an artificial skylight on a daylight curve.'],
  ['A ninety second reset', 'Staff clean from a corridor behind the wall. Nobody walks through your room while you are in it.'],
  ['A foot basin in every room', 'For washing before prayer. There are prayer rugs in the Hull and a place to use them.'],
  ['The Hull', 'A free room in the middle with a brick floor, church pews and a planted wall. No pass, no purchase, no explanation.'],
];

const BuildPublicPage: React.FC = () => (
  <>
    <Helmet>
      <title>Maslow — a private restroom in SoHo</title>
      <meta
        name="description"
        content="Twelve private restrooms in SoHo wrapped around a free room called the Hull. Opening around 2028. Join the list."
      />
    </Helmet>

    <div style={{ backgroundColor: CREAM, color: NAVY }}>
      {/* Hero */}
      <section style={{ padding: '72px 20px 48px', textAlign: 'center' }}>
        <p style={{ fontFamily: SANS, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', color: MUTED, margin: '0 0 20px' }}>
          SoHo &middot; opening around 2028
        </p>
        <h1 style={{ fontFamily: SERIF, fontSize: 'clamp(34px, 6vw, 60px)', lineHeight: 1.1, fontWeight: 400, margin: '0 auto 22px', maxWidth: 760 }}>
          A door that locks, and ten minutes that belong to you.
        </h1>
        <p style={{ fontFamily: SANS, fontSize: 'clamp(16px, 2vw, 19px)', lineHeight: 1.65, color: MUTED, maxWidth: 620, margin: '0 auto 14px' }}>
          Twelve private restrooms in SoHo, wrapped around a free room called the Hull.
          You pay for a room by the session. The Hull is free to everyone, always.
        </p>
        <p style={{ fontFamily: SANS, fontSize: 15, lineHeight: 1.6, color: MUTED, maxWidth: 620, margin: '0 auto 36px' }}>
          Nothing is on sale yet. Put your email in and I will tell you when it is.
        </p>
        <WaitlistForm source="build" showPosition />
      </section>

      {/* Plan */}
      <section style={{ padding: '8px 20px 56px', maxWidth: 900, margin: '0 auto' }}>
        <h2 style={{ fontFamily: SANS, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: MUTED, fontWeight: 600, margin: '0 0 18px', textAlign: 'center' }}>
          The plan, from above
        </h2>
        <img
          src="/maslow-plan.svg"
          alt="Floor plan: twelve rooms in a horseshoe around the Hull, a staff corridor behind them, and the whole street front open."
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
        <p style={{ fontFamily: SANS, fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 18, textAlign: 'center' }}>
          The rooms make a horseshoe around the Hull and the entire street front is open. Staff reset
          each room from a corridor guests never see.
        </p>
      </section>

      {/* Facts */}
      <section style={{ padding: '0 20px 72px', maxWidth: 760, margin: '0 auto' }}>
        {FACTS.map(([title, body]) => (
          <div key={title} style={{ borderTop: `1px solid ${RULE}`, padding: '20px 0' }}>
            <h3 style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 400, margin: '0 0 6px' }}>{title}</h3>
            <p style={{ fontFamily: SANS, fontSize: 15.5, lineHeight: 1.65, color: MUTED, margin: 0 }}>{body}</p>
          </div>
        ))}
      </section>

      {/* Close */}
      <section style={{ backgroundColor: NAVY, color: CREAM, padding: '64px 20px', textAlign: 'center' }}>
        <p style={{ fontFamily: SERIF, fontSize: 'clamp(22px, 3.4vw, 30px)', lineHeight: 1.35, margin: '0 auto 28px', maxWidth: 620 }}>
          Premium is the floor, not an upgrade.
        </p>
        <p style={{ fontFamily: SANS, fontSize: 15.5, lineHeight: 1.65, color: 'color-mix(in srgb, var(--cream) 78%, transparent)', maxWidth: 560, margin: '0 auto 32px' }}>
          Every room is identical. Ten minutes costs less than a macchiato, and that guest gets the same
          room as a founding member, with the same fixtures and the same light.
        </p>
        <WaitlistForm source="build_footer" onDark />
      </section>
    </div>
  </>
);

export default BuildPublicPage;
